import { useEffect, useState } from 'react';

import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { IconMenu2, IconX } from '@tabler/icons-react';
import { MainRouteKeys, useGetMainPageUrl } from 'app-level/navigation';
import { NavLink, useLocation } from 'react-router-dom';

import { HeaderLogo } from './HeaderLogo';
import { MAIN_HEADER_CONFIG } from './headers.config';
import { MainNavIcon } from './MainNavIcon';
import { useMediaQuery, WIDE_ENOUGH_FOR_INLINE_NAV } from './useMediaQuery';
import { isTabActive, useSubHeaderTabs } from './useSubHeaderTabs';

export function MobileNavDrawer() {
  const [isOpen, setIsOpen] = useState(false);
  const { pathname } = useLocation();
  const getPath = useGetMainPageUrl();
  const subHeader = useSubHeaderTabs();
  const isWide = useMediaQuery(WIDE_ENOUGH_FOR_INLINE_NAV);

  const { refs, context } = useFloating({ open: isOpen, onOpenChange: setIsOpen });
  const dismiss = useDismiss(context, { outsidePressEvent: 'mousedown' });
  const role = useRole(context, { role: 'dialog' });
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, role]);

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (isWide) setIsOpen(false);
  }, [isWide]);

  return (
    <>
      <button
        type="button"
        aria-label="Open menu"
        data-tour={isWide ? undefined : 'home-nav'}
        ref={refs.setReference}
        {...getReferenceProps({ onClick: () => setIsOpen(true) })}
        className="flex h-11 w-11 flex-none items-center justify-center rounded bg-gray-950 text-white md:hidden"
      >
        <IconMenu2 />
      </button>
      {isOpen && (
        <FloatingPortal>
          <FloatingOverlay lockScroll className="z-50 bg-black/60 md:hidden">
            <FloatingFocusManager context={context}>
              <div
                ref={refs.setFloating}
                {...getFloatingProps()}
                aria-label="Main menu"
                className="flex h-full w-[min(320px,85vw)] flex-col bg-slate-950 text-white shadow-[8px_0_24px_rgba(0,0,0,0.6)] light:bg-white light:text-gray-700"
              >
                <div className="flex h-16 flex-none items-center justify-between px-4">
                  <HeaderLogo />
                  <button
                    type="button"
                    aria-label="Close menu"
                    onClick={() => setIsOpen(false)}
                    className="flex h-11 w-11 items-center justify-center rounded bg-gray-950 text-white"
                  >
                    <IconX />
                  </button>
                </div>
                <nav aria-label="Main" className="flex-1 overflow-y-auto pb-4">
                  <ul className="m-0 p-0">
                    {MAIN_HEADER_CONFIG.map((item) => {
                      const routeKey = item.routeKey as MainRouteKeys;
                      const isCurrentSection = subHeader?.mainRouteKey === routeKey;
                      return (
                        <li key={routeKey} className="list-none">
                          <NavLink
                            to={getPath(routeKey)}
                            onClick={() => setIsOpen(false)}
                            className={({ isActive }) =>
                              `flex h-12 items-center gap-3 px-4 text-base no-underline ${
                                isActive
                                  ? 'bg-white/5 text-foreground-default light:text-black'
                                  : 'text-gray-400 hover:text-white light:text-gray-600 light:hover:text-black'
                              }`
                            }
                          >
                            {item.iconName && <MainNavIcon name={item.iconName} size="large" />}
                            {item.label}
                          </NavLink>
                          {isCurrentSection && subHeader.tabs.length > 0 && (
                            <ul className="m-0 p-0">
                              {subHeader.tabs.map((tab) => (
                                <li key={tab.key} className="list-none">
                                  <NavLink
                                    to={tab.to}
                                    onClick={() => setIsOpen(false)}
                                    className={`flex h-11 items-center border-l-2 pl-12 text-sm no-underline ${
                                      tab.disabled
                                        ? 'pointer-events-none border-transparent-0 text-gray-500'
                                        : isTabActive(tab, pathname)
                                          ? 'border-accent-default text-foreground-default light:text-black'
                                          : 'border-transparent-0 text-gray-400 hover:text-white light:text-gray-600 light:hover:text-black'
                                    }`}
                                  >
                                    {tab.label}
                                  </NavLink>
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </nav>
              </div>
            </FloatingFocusManager>
          </FloatingOverlay>
        </FloatingPortal>
      )}
    </>
  );
}
